'use client';
import { GtmListIdType } from '@vs-shop/schemas/gtm/list-types';
import { ProductAccessoriesPanel } from '@vs-shop/commerce-ui/productAccessories';
import { PaginationLoadMore, PaginationLoadMoreButton } from '@vs-shop/commerce-ui/pagination';
import { Option } from 'effect';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import { productRecommendedProductsPerPageDefault } from '../../../../../config/pagination';

import { accessoriesPanelId, accessoriesTabId } from './accessoriesIds';

import AccessoriesPagination from '../../../../client/products/AccessoriesPagination';
import RecommendationsGrid from '../../../../client/products/RecommendationsGrid';

import {
  accessoriesLoadMorePagesAtom,
  accessoriesPageSearchParamAtom,
  changeAccessoriesPageAtomFn,
} from '../../../../../effect/atom/products/recommendationsPagination';
import type { ProductBase } from '../../../../../effect/schema/openapi/backend';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { useAtomSet } from '@effect/atom-react';

type Props = {
  taxonCode: string;
  productCode: string;
  initialTaxon: string | null;
  baseUrl: string;
  productsResponse: AsyncResult.AsyncResult<
    HydraCollectionResponseType<typeof ProductBase>,
    unknown
  >;
  lastPageLoadedIncludingLoadMore: number;
};

/**
 * One group of accessories: the shared panel part filled with this tenant's
 * grid, its pager and its load-more control.
 *
 * The grid and the pager are rcprofi's own components — both read the
 * accessories atoms — and the control that appends a page is the shared
 * pagination part, the same one the category listing composes.
 */
export default function RecommendationTab({
  taxonCode,
  productCode,
  initialTaxon,
  baseUrl,
  productsResponse,
  lastPageLoadedIncludingLoadMore,
}: Props) {
  const intl = useIntl();

  const changePage = useAtomSet(changeAccessoriesPageAtomFn, {
    mode: 'promise',
  });

  const setPageSearchParam = useAtomSet(accessoriesPageSearchParamAtom, {
    mode: 'value',
  });

  const setLoadMorePages = useAtomSet(accessoriesLoadMorePagesAtom, {
    mode: 'value',
  });

  const ref = useRef<HTMLDivElement>(null);

  function scrollToAccessoriesGrid(): void {
    if (!ref.current) return;

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    });
  }

  return (
    <ProductAccessoriesPanel
      key={taxonCode}
      value={taxonCode}
      id={accessoriesPanelId(taxonCode)}
      aria-labelledby={accessoriesTabId(taxonCode)}
    >
      <div ref={ref}>
        <RecommendationsGrid
          productCode={productCode}
          listType={GtmListIdType.PRODUCT_RECOMMENDATIONS}
          itemsPerPage={productRecommendedProductsPerPageDefault}
          initialTaxon={initialTaxon}
        />

        {AsyncResult.builder(productsResponse)
          .onSuccess((response) =>
            Option.match(Option.fromNullishOr(response['hydra:view']), {
              onNone: () => null,
              onSome: (hydraViewExtended) => (
                <>
                  <AccessoriesPagination
                    hydraViewExtended={hydraViewExtended}
                    changePage={changePage}
                    scrollToAccessoriesGrid={scrollToAccessoriesGrid}
                    baseUrl={baseUrl}
                  />

                  {lastPageLoadedIncludingLoadMore < hydraViewExtended.hydraViewNumbers.last && (
                    <PaginationLoadMore>
                      <PaginationLoadMoreButton
                        onClick={() => {
                          setPageSearchParam((currentPageOption) => {
                            const currentPage = Option.getOrElse(currentPageOption, () => 1);
                            return Option.some(currentPage + 1);
                          });
                          setLoadMorePages((loadMorePages) => loadMorePages + 1);
                        }}
                      >
                        {intl.formatMessage({
                          id: 'component.server.page.product.recommendations.loadMore',
                          defaultMessage: 'ZOBRAZIT DALŠÍ',
                        })}
                      </PaginationLoadMoreButton>
                    </PaginationLoadMore>
                  )}
                </>
              ),
            })
          )
          .render()}
      </div>
    </ProductAccessoriesPanel>
  );
}
